import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CodeBracketIcon,
  CpuChipIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Navbar, Footer, CourseCard } from './ui';
import LoadingSpinner from './LoadingSpinner';
import apiService from '../services/api';

const CourseListPage = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const coursesData = await apiService.getCourses();
        
        // Check if coursesData is an array
        if (!Array.isArray(coursesData)) {
          console.error('Courses data is not an array:', coursesData);
          setError('Invalid course data format received from server');
          return;
        }
        
        if (coursesData.length === 0) {
          console.warn('No courses returned from API');
          setError('No courses available at the moment');
          return;
        }
        
        // Transform course data to match display format
        const transformedCourses = coursesData.map((course, index) => {
          // Map course categories to icons and colors
          const courseIcons = [
            { icon: ChartBarIcon, color: "from-blue-500 to-purple-600", category: "Data Science" },
            { icon: CodeBracketIcon, color: "from-green-500 to-teal-600", category: "Software Engineering" },
            { icon: ShieldCheckIcon, color: "from-red-500 to-orange-600", category: "Cybersecurity" },
            { icon: CpuChipIcon, color: "from-purple-500 to-pink-600", category: "AI/ML" },
            { icon: GlobeAltIcon, color: "from-orange-500 to-red-600", category: "Web Development" },
            { icon: AcademicCapIcon, color: "from-indigo-500 to-blue-600", category: "Digital Marketing" }
          ];
          
          const iconConfig = courseIcons[index % courseIcons.length];
          
          return {
            title: course.name,
            description: course.description,
            icon: iconConfig.icon,
            duration: `${course.duration_months} months`,
            level: "Beginner to Advanced",
            students: `${Math.floor(Math.random() * 3000) + 500}+`,
            color: iconConfig.color,
            category: iconConfig.category,
            features: ["Hands-on Projects", "Expert Mentorship", "Career Support", "Industry Recognition"],
            cost_amount: course.cost_amount,
            cost_currency: course.cost_currency
          };
        });

        setCourses(transformedCourses);
        setFilteredCourses(transformedCourses);

      } catch (err) {
        console.error('Failed to load courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  // Filter courses based on search and category
  useEffect(() => {
    let filtered = courses;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    setFilteredCourses(filtered);
  }, [searchTerm, selectedCategory, courses]);

  // Get unique categories for filter dropdown
  const categories = ['all', ...new Set(courses.map(course => course.category))];

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Courses', to: '/courses' },
    { label: 'Contact', to: '#contact' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar links={navLinks} />
        <div className="pt-24 pb-12 flex items-center justify-center">
          <LoadingSpinner size="large" message="Loading courses..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar links={navLinks} />
        <div className="pt-24 pb-12 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar links={navLinks} />

      {/* Header Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-6">
              <Link 
                to="/" 
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Back to Home
              </Link>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              All <span className="text-primary-600">Courses</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our complete catalog of {courses.length} professional courses designed to transform your career
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search courses..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                className="block w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.slice(1).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-500">
              {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map((course, index) => (
                <CourseCard 
                  key={index} 
                  course={course}
                  linkTo={`/course/${course.id || (course.title || course.name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CourseListPage; 